// Recurring daily cutoff with explicit lifecycle cleanup for theme-editor re-renders.
if (!customElements.get('fresh-market-countdown')) {
  class FreshMarketCountdown extends HTMLElement {
    connectedCallback() {
      this.timeZone = this.dataset.timeZone || 'Australia/Sydney';
      this.hour = Number.parseInt(this.dataset.hour, 10);
      this.minute = Number.parseInt(this.dataset.minute, 10);
      this.hoursElement = this.querySelector('[data-hours]');
      this.minutesElement = this.querySelector('[data-minutes]');
      this.secondsElement = this.querySelector('[data-seconds]');

      if (!Number.isInteger(this.hour) || !Number.isInteger(this.minute)) return;
      if (!this.hoursElement || !this.minutesElement || !this.secondsElement) return;

      try {
        this.formatter = this.createFormatter(this.timeZone);
      } catch {
        this.timeZone = 'Australia/Sydney';
        this.formatter = this.createFormatter(this.timeZone);
      }

      this.update = this.update.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      this.start();
    }

    disconnectedCallback() {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      this.stop();
    }

    createFormatter(timeZone) {
      return new Intl.DateTimeFormat('en-AU', {
        timeZone,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23'
      });
    }

    start() {
      this.stop();
      this.update();
      this.timer = window.setInterval(this.update, 1000);
    }

    stop() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.stop();
      } else {
        this.start();
      }
    }

    update() {
      const parts = Object.fromEntries(
        this.formatter
          .formatToParts(new Date())
          .filter((part) => part.type !== 'literal')
          .map((part) => [part.type, Number.parseInt(part.value, 10)])
      );
      const nowInSeconds = parts.hour * 3600 + parts.minute * 60 + parts.second;
      const deadlineInSeconds = this.hour * 3600 + this.minute * 60 + 59;
      // This is a recurring daily cutoff in the configured time zone, not a one-time campaign deadline.
      const remaining = (deadlineInSeconds - nowInSeconds + 86400) % 86400;
      this.hoursElement.textContent = String(Math.floor(remaining / 3600)).padStart(2, '0');
      this.minutesElement.textContent = String(Math.floor((remaining % 3600) / 60)).padStart(2, '0');
      this.secondsElement.textContent = String(remaining % 60).padStart(2, '0');
    }
  }

  customElements.define('fresh-market-countdown', FreshMarketCountdown);
}

// Owns accessible Tab/panel state and the bounded horizontal rail interaction.
if (!customElements.get('fresh-market-slider')) {
  class FreshMarketSlider extends HTMLElement {
    connectedCallback() {
      this.shopAllLink = this.querySelector('[data-fresh-market-shop-all]');
      this.tabs = [...this.querySelectorAll('[data-fresh-market-tab]')];
      this.panels = [...this.querySelectorAll('[data-fresh-market-panel]')];
      this.status = this.querySelector('[data-fresh-market-status]');

      if (!this.tabs.length || !this.panels.length) return;

      this.abortController = new AbortController();
      this.handleClick = this.handleClick.bind(this);
      this.handleTabKeydown = this.handleTabKeydown.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleTrackScroll = this.handleTrackScroll.bind(this);
      this.updateControls = this.updateControls.bind(this);

      this.panelById = new Map(
        this.panels.filter((panel) => panel.id).map((panel) => [panel.id, panel])
      );

      this.addEventListener('click', this.handleClick, {
        signal: this.abortController.signal
      });
      this.addEventListener('keydown', this.handleTabKeydown, {
        signal: this.abortController.signal
      });
      this.addEventListener('shopify:block:select', this.handleBlockSelect, {
        signal: this.abortController.signal
      });

      this.resizeObserver = new ResizeObserver(this.updateControls);
      this.panels.forEach((panel) => {
        const track = panel.querySelector('[data-fresh-market-track]');
        if (!track) return;

        track.addEventListener('scroll', this.handleTrackScroll, {
          passive: true,
          signal: this.abortController.signal
        });
        this.resizeObserver.observe(track);
      });

      this.selectTab(this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true'), false);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      if (this.updateFrame) window.cancelAnimationFrame(this.updateFrame);
    }

    handleClick(event) {
      const tab = event.target.closest('[data-fresh-market-tab]');
      if (tab && this.contains(tab)) {
        this.selectTab(tab);
        return;
      }

      const control = event.target.closest('[data-fresh-market-next]');
      if (!control || !this.contains(control)) return;

      const panel = control.closest('[data-fresh-market-panel]');
      const track = panel?.querySelector('[data-fresh-market-track]');
      const card = track?.querySelector('.fresh-market__card');
      if (!track || !card) return;

      const styles = window.getComputedStyle(track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
      const rtlFactor = document.documentElement.dir === 'rtl' ? -1 : 1;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      track.scrollTo({
        left: track.scrollLeft + rtlFactor * (card.getBoundingClientRect().width + gap),
        behavior: reducedMotion ? 'auto' : 'smooth'
      });
    }

    handleTabKeydown(event) {
      const currentIndex = this.tabs.indexOf(event.target);
      if (currentIndex < 0) return;

      const lastIndex = this.tabs.length - 1;
      let nextIndex;

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        nextIndex = currentIndex === lastIndex ? 0 : currentIndex + 1;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        nextIndex = currentIndex === 0 ? lastIndex : currentIndex - 1;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = lastIndex;
      } else {
        return;
      }

      event.preventDefault();
      this.tabs[nextIndex].focus();
      this.selectTab(this.tabs[nextIndex]);
    }

    handleBlockSelect(event) {
      const tab = event.target.closest?.('[data-fresh-market-tab]');
      if (!tab || !this.contains(tab)) return;

      this.selectTab(tab, false);
      this.scrollTabIntoView(tab);
    }

    handleTrackScroll(event) {
      if (event.currentTarget.closest('[data-fresh-market-panel]') === this.activePanel) {
        this.updateControls();
      }
    }

    selectTab(activeTab, announce = true) {
      if (!activeTab) return;

      const activeLabel = activeTab.textContent.trim();
      const controlledPanelId = activeTab.getAttribute('aria-controls');
      const activePanel = this.panelById.get(controlledPanelId);
      if (!activePanel) return;

      if (this.shopAllLink) {
        const targetLink = activeTab.dataset.freshMarketLink;
        if (targetLink) this.shopAllLink.href = targetLink;
        this.shopAllLink.setAttribute('aria-label', `${this.shopAllLink.textContent.trim()}: ${activeLabel}`);
      }

      this.tabs.forEach((tab) => {
        const isActive = tab === activeTab;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', String(isActive));
        tab.tabIndex = isActive ? 0 : -1;
      });

      if (announce) this.scrollTabIntoView(activeTab);

      this.panels.forEach((panel) => {
        panel.hidden = panel !== activePanel;
      });

      this.activePanel = activePanel;
      const track = activePanel.querySelector('[data-fresh-market-track]');
      const emptyMessage = activePanel.querySelector('[data-fresh-market-empty]');
      const visibleCount = track?.querySelectorAll('.fresh-market__card').length || 0;

      if (track) {
        track.hidden = visibleCount === 0;
        track.scrollTo({ left: 0, behavior: 'auto' });
      }
      if (emptyMessage) emptyMessage.hidden = visibleCount !== 0;

      if (announce && this.status) {
        this.status.textContent = `${activeLabel}: ${visibleCount}`;
      }

      this.updateControls();
    }

    scrollTabIntoView(tab) {
      const tabList = tab.parentElement;
      if (!tabList) return;

      const listRect = tabList.getBoundingClientRect();
      const tabRect = tab.getBoundingClientRect();
      const leftDelta = tabRect.left < listRect.left ? tabRect.left - listRect.left : 0;
      const rightDelta = tabRect.right > listRect.right ? tabRect.right - listRect.right : 0;
      const delta = leftDelta || rightDelta;

      if (delta) tabList.scrollTo({ left: tabList.scrollLeft + delta, behavior: 'auto' });
    }

    updateControls() {
      if (this.updateFrame) return;

      this.updateFrame = window.requestAnimationFrame(() => {
        this.panels.forEach((panel) => {
          const track = panel.querySelector('[data-fresh-market-track]');
          const nextButton = panel.querySelector('[data-fresh-market-next]');
          if (!track || !nextButton) return;

          const maximumScroll = Math.max(0, track.scrollWidth - track.clientWidth);
          const currentScroll = Math.abs(track.scrollLeft);
          const hasOverflow = !panel.hidden && !track.hidden && maximumScroll > 1;
          nextButton.hidden = !hasOverflow || currentScroll >= maximumScroll - 1;
        });

        this.updateFrame = null;
      });
    }
  }

  customElements.define('fresh-market-slider', FreshMarketSlider);
}
